import { useCallback, useRef } from "react";
import { EmojiReaction, Comment, CommentReaction } from "../types";

export const useRetrospectiveHandlers = (
  nodes: any[],
  setNodes: any,
  setEdges: any,
  edges: any[],
  emit: any,
  userId: string,
  userName: string,
  userColor: string,
  users: Map<string, any>,
  markUserChange: () => void,
  sendEmojiReactionAdd: any,
  sendEmojiReactionRemove: any,
  sendCommentAdd: any,
  sendCommentReactionAdd: any,
  sendCommentReactionRemove: any,
  updateTextChange: any,
  startNodeEditing: any,
  endNodeEditing: any,
  updateNodeDragPosition: any,
  endNodeDrag: any,
  onNodesChange: any,
  onEdgesChange: any
) => {
  const draggedNodesRef = useRef<Set<string>>(new Set());

  const handleNodeDelete = useCallback(
    (nodeId: string) => {
      setNodes((nds: any[]) => nds.filter((node) => node.id !== nodeId));
      markUserChange();

      emit("NODE_DELETED", {
        nodeId,
        deletedBy: userId,
      });
    },
    [setNodes, emit, userId, markUserChange]
  );

  const handleEdgeDelete = useCallback(
    (edgeId: string) => {
      setEdges((eds: any[]) => eds.filter((edge) => edge.id !== edgeId));
      markUserChange();

      emit("EDGE_DELETED", {
        edgeId,
        deletedBy: userId,
      });
    },
    [setEdges, emit, userId, markUserChange]
  );

  const handleNodeColorChange = useCallback(
    (nodeId: string, color: string) => {
      setNodes((nds: any[]) =>
        nds.map((node) =>
          node.id === nodeId ? { ...node, data: { ...node.data, color } } : node
        )
      );
      markUserChange();

      emit("NODE_COLOR_UPDATED", {
        nodeId,
        color,
        updatedBy: userId,
      });
    },
    [setNodes, emit, userId, markUserChange]
  );

// Handle node text changes - Fixed to prevent focus loss
const handleNodeTextChange = useCallback(
    (nodeId: string, text: string) => {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeId ? { ...node, data: { ...node.data, text } } : node
        )
      );
      markUserChange();
      emit("NODE_TEXT_UPDATED", {
        nodeId,
        text,
        userId: userId,
      });
    },
    [setNodes, emit, userId, markUserChange]
  );

  // Handle real-time text changes - Fixed to maintain focus
  const handleNodeTextChangeRealtime = useCallback(
    (nodeId: string, text: string) => {
      updateTextChange(nodeId, text);
      emit("NODE_TEXT_CHANGED", {
        nodeId,
        text,
        userId: userId,
      });
    },
    [updateTextChange]
  );

  const handleNodeEditStart = useCallback(
    (nodeId: string, editType: "text" | "color" | "general") => {
      startNodeEditing(nodeId, editType);
    },
    [startNodeEditing]
  );

  const handleNodeEditEnd = useCallback(
    (nodeId: string) => {
      endNodeEditing(nodeId);
    },
    [endNodeEditing]
  );

  const handleNodeEmojiReactionAdd = useCallback(
    (nodeId: string, emoji: string, userIdCb: string) => {
      const newReaction: EmojiReaction = {
        emoji,
        userId: userIdCb,
        userName: users.get(userIdCb)?.name || userName,
        timestamp: Date.now(),
      };

      setNodes((nds: any[]) =>
        nds.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                data: {
                  ...node.data,
                  reactions: [...(node.data.reactions || []), newReaction],
                },
              }
            : node
        )
      );

      sendEmojiReactionAdd(nodeId, newReaction);
      if (userIdCb === userId) markUserChange();
    },
    [setNodes, sendEmojiReactionAdd, users, userName, userId, markUserChange]
  );

  const handleNodeEmojiReactionToggle = useCallback(
    (nodeId: string, emoji: string, reactionUserId: string) => {
      setNodes((nds: any[]) =>
        nds.map((node) => {
          if (node.id !== nodeId) return node;

          const reactions = node.data.reactions || [];
          const existingReactionIndex = reactions.findIndex(
            (reaction: any) =>
              reaction.emoji === emoji && reaction.userId === reactionUserId
          );

          let newReactions;
          if (existingReactionIndex >= 0) {
            newReactions = reactions.filter(
              (_: any, index: number) => index !== existingReactionIndex
            );
            sendEmojiReactionRemove(nodeId, emoji, reactionUserId);
          } else {
            const newReaction: EmojiReaction = {
              emoji,
              userId: reactionUserId,
              userName: users.get(reactionUserId)?.name || userName,
              timestamp: Date.now(),
            };
            newReactions = [...reactions, newReaction];
            sendEmojiReactionAdd(nodeId, newReaction);
          }

          return {
            ...node,
            data: {
              ...node.data,
              reactions: newReactions,
            },
          };
        })
      );

      if (reactionUserId === userId) markUserChange();
    },
    [
      setNodes,
      sendEmojiReactionAdd,
      sendEmojiReactionRemove,
      users,
      userName,
      userId,
      markUserChange,
    ]
  );

  const handleAddComment = useCallback(
    (nodeId: string, text: string) => {
      const newComment: Comment = {
        id: `comment-${Date.now()}-${userId}`,
        text,
        userId: userId,
        userName: userName,
        userColor: userColor,
        timestamp: Date.now(),
        reactions: [],
      };

      setNodes((nds: any[]) =>
        nds.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                data: {
                  ...node.data,
                  comments: [...(node.data.comments || []), newComment],
                },
              }
            : node
        )
      );

      markUserChange();
      sendCommentAdd(nodeId, newComment);
    },
    [setNodes, userId, userName, userColor, markUserChange, sendCommentAdd]
  );

  const handleAddCommentReaction = useCallback(
    (nodeId: string, commentId: string, emoji: string) => {
      const newReaction: CommentReaction = {
        emoji,
        userId: userId,
        userName: userName,
        timestamp: Date.now(),
      };

      setNodes((nds: any[]) =>
        nds.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                data: {
                  ...node.data,
                  comments: (node.data.comments || []).map((comment: any) =>
                    comment.id === commentId
                      ? {
                          ...comment,
                          reactions: [
                            ...(comment.reactions || []),
                            newReaction,
                          ],
                        }
                      : comment
                  ),
                },
              }
            : node
        )
      );

      markUserChange();
      sendCommentReactionAdd(nodeId, commentId, newReaction);
    },
    [setNodes, userId, userName, markUserChange, sendCommentReactionAdd]
  );

  const handleRemoveCommentReaction = useCallback(
    (
      nodeId: string,
      commentId: string,
      emoji: string,
      reactionUserId: string
    ) => {
      setNodes((nds: any[]) =>
        nds.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                data: {
                  ...node.data,
                  comments: (node.data.comments || []).map((comment: any) =>
                    comment.id === commentId
                      ? {
                          ...comment,
                          reactions: (comment.reactions || []).filter(
                            (reaction: any) =>
                              !(
                                reaction.emoji === emoji &&
                                reaction.userId === reactionUserId
                              )
                          ),
                        }
                      : comment
                  ),
                },
              }
            : node
        )
      );

      markUserChange();
      sendCommentReactionRemove(nodeId, commentId, emoji, reactionUserId);
    },
    [setNodes, markUserChange, sendCommentReactionRemove]
  );

  const handleNodesChange = useCallback(
    (changes: any[], customLogic?: (changes: any[]) => void) => {
      onNodesChange(changes);

      let hasUserChange = false;

      changes.forEach((change) => {
        if (change.type === "position" && change.position) {
          if (change.dragging) {
            draggedNodesRef.current.add(change.id);
            updateNodeDragPosition(change.id, change.position);
          } else if (draggedNodesRef.current.has(change.id)) {
            draggedNodesRef.current.delete(change.id);
            endNodeDrag(change.id);
            emit("NODE_POSITION_UPDATED", {
              nodeId: change.id,
              position: change.position,
              updatedBy: userId,
            });
            hasUserChange = true;
          }
        }

        if (change.type === "dimensions" && change.dimensions) {
          const updatedNode = nodes.find((n) => n.id === change.id);
          if (updatedNode) {
            emit("NODE_DIMENSIONS_UPDATED", {
              nodeId: change.id,
              dimensions: {
                width: change.dimensions.width,
                height: change.dimensions.height,
              },
              updatedBy: userId,
            });
            hasUserChange = true;
          }
        }

        if (change.type === "remove") {
          draggedNodesRef.current.delete(change.id);
          emit("NODE_DELETED", {
            nodeId: change.id,
            deletedBy: userId,
          });
          hasUserChange = true;
        }
      });

      // Execute custom logic if provided
      if (customLogic) {
        customLogic(changes);
      }

      if (hasUserChange) markUserChange();
    },
    [
      onNodesChange,
      emit,
      userId,
      updateNodeDragPosition,
      endNodeDrag,
      nodes,
      markUserChange,
    ]
  );

  const handleEdgesChange = useCallback(
    (changes: any[]) => {
      onEdgesChange(changes);

      let hasUserChange = false;
      changes.forEach((change) => {
        if (change.type === "remove") {
          emit("EDGE_DELETED", {
            edgeId: change.id,
            deletedBy: userId,
          });
          hasUserChange = true;
        }
      });

      if (hasUserChange) markUserChange();
    },
    [onEdgesChange, emit, userId, markUserChange]
  );

  return {
    handleNodeDelete,
    handleEdgeDelete,
    handleNodeColorChange,
    handleNodeTextChange,
    handleNodeTextChangeRealtime,
    handleNodeEditStart,
    handleNodeEditEnd,
    handleNodeEmojiReactionAdd,
    handleNodeEmojiReactionToggle,
    handleAddComment,
    handleAddCommentReaction,
    handleRemoveCommentReaction,
    handleNodesChange,
    handleEdgesChange,
  };
};
